import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { formatApiDateTime } from 'src/utils/taskDateUtils';
import { COLORS } from './styles';

/**
 * ISS-025B — confirmation for "Ignore" on an archived lead's re-enquiry.
 *
 * WHY A CONFIRM AT ALL, when the original plan called for a direct action:
 * the operation is easy to trigger by accident (a 28px hover target on a
 * dense list) and awkward to walk back. Ignoring closes AND re-archives the
 * thread, so it leaves every inbox tab; reopening it from the Resolved view
 * sets `is_closed=false` but the thread is still archived with a NULL
 * `re_engaged_at`, so it stays hidden. In practice the only reliable way back
 * is for the patient to message again. One sentence of confirmation is cheap
 * insurance against a mis-click that silently drops a live enquiry.
 *
 * WHY IT IS SHARED between the chat banner and the sidebar row: the whole
 * problem this ticket fixes is that the same operation was spelled three
 * different ways ("Dismiss", an archive icon, "Close Conversation") and reps
 * could not predict what any of them did. The consequence sentence therefore
 * lives in exactly one file. If it changes, it changes everywhere.
 *
 * Purely presentational — the caller owns the thunk dispatch and the busy
 * state, so this component stays trivially testable.
 */
const IgnoreReEngagementDialog = ({
    open,
    onClose,
    onConfirm,
    contactName = '',
    archivedAt = null,
    busy = false,
}) => {
    const who = contactName?.trim() || 'This patient';
    const archivedLabel = formatApiDateTime(archivedAt);

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            slotProps={{ paper: { sx: { borderRadius: '12px' } } }}
            aria-labelledby="ignore-re-engagement-title"
        >
            <DialogTitle
                id="ignore-re-engagement-title"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem', fontWeight: 600, pb: 1 }}
            >
                <Icon icon="mdi:eye-off-outline" width={20} height={20} style={{ color: COLORS.warningDark }} />
                Hide this conversation?
            </DialogTitle>
            <DialogContent sx={{ pb: 1 }}>
                <Typography sx={{ fontSize: 14, color: COLORS.textPrimary, lineHeight: 1.5 }}>
                    {who} stays archived and this chat leaves your inbox, so you
                    won&apos;t be able to reply.
                </Typography>
                {/* Second line, deliberately: the first states what the rep is
                    giving up, the second gives it back. Reps hesitated on
                    "Dismiss" precisely because nothing told them the
                    conversation was recoverable. Keep "the chat" as the
                    subject — an "it" here reads as the patient. */}
                <Typography sx={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5, mt: 1 }}>
                    The chat comes back on its own if they message again.
                </Typography>
                {archivedLabel && (
                    <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mt: 1.5 }}>
                        Archived {archivedLabel}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
                <Button size="small" onClick={onClose} disabled={busy} sx={{ color: COLORS.textSecondary }}>
                    Cancel
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={onConfirm}
                    disabled={busy}
                    sx={{
                        backgroundColor: COLORS.warningDark,
                        '&:hover': { backgroundColor: COLORS.warningDark, filter: 'brightness(0.92)' },
                    }}
                >
                    Hide it
                </Button>
            </DialogActions>
        </Dialog>
    );
};

IgnoreReEngagementDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    contactName: PropTypes.string,
    archivedAt: PropTypes.string,
    busy: PropTypes.bool,
};

export default IgnoreReEngagementDialog;
